/**
 * Pure path helpers for tenant URLs (ADR-0302). No React, so plain utilities
 * (`utils/pageContext.ts`) can use them; the navigation wrappers in
 * `accountRouting.tsx` build on them.
 */

/** The route prefix of a tenant URL: `/a/:accountPublicId`. */
export const TENANT_ROUTE_BASE = "/a/:accountPublicId";

/**
 * Absolute paths that are NOT inside an account, so the wrappers leave them
 * alone. Everything else that starts with `/` is a tenant path.
 *
 * - sign-in and session pages, OAuth callbacks (their redirect URIs are
 *   registered with the providers and cannot carry an account);
 * - `/docs`, universal and gated by the session account;
 * - superadmin and partner-admin pages that span accounts (`/settings/clients`,
 *   `/settings/partners`) or no account at all (`/platform-settings`);
 * - dev-only rigs and demo pages.
 */
export const UNPREFIXED_ROOTS: readonly string[] = [
  "/login",
  "/logout",
  "/auth",
  "/accept-invite",
  "/docs",
  "/settings/clients",
  "/settings/partners",
  "/platform-settings",
  "/demo",
  "/testcode",
  "/loaders",
  "/checkbox-lab",
  "/toggle-lab",
  "/motion-lab",
  "/onsite-lab",
  "/onsite-templates-lab",
];

const TENANT_PREFIX = /^\/a\/[^/?#]+/;

function pathnameOf(path: string): string {
  const end = path.search(/[?#]/);
  return end === -1 ? path : path.slice(0, end);
}

/** True for an absolute path that belongs inside an account. */
export function isTenantPath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  const pathname = pathnameOf(path);
  if (pathname === "/a" || pathname.startsWith("/a/")) return false;
  return !UNPREFIXED_ROOTS.some(
    (root) => pathname === root || pathname.startsWith(`${root}/`),
  );
}

/**
 * `/activation/segments?x=1` → `/a/<account>/activation/segments?x=1`.
 * Relative paths, already-prefixed paths and unprefixed roots are returned
 * unchanged, and so is everything when there is no account to put in front.
 */
export function tenantPath(path: string, account: string | null | undefined): string {
  if (!account || !isTenantPath(path)) return path;
  return path === "/" || path.startsWith("/?") || path.startsWith("/#")
    ? `/a/${account}${path.slice(1)}`
    : `/a/${account}${path}`;
}

/** The pathname without its `/a/<account>` prefix. Unprefixed paths come back as they are. */
export function stripTenantPrefix(pathname: string): string {
  const stripped = pathname.replace(TENANT_PREFIX, "");
  return stripped === pathname ? pathname : stripped || "/";
}
